import React, { useEffect, useRef, useState } from 'react';
import { MbmProvider } from '@amzn/react-arb-tools';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { DEFAULT_LOCALE, METRICS_EVENTS } from '../constants';
import arbManifest from 'translations/arbManifest';
import { resolveBundleUrlWithFallbacks } from '../helpers';
import ChatPage from "../pages/chat/chat-page";
import ShoulderTapPage from "../pages/shoulder-tap/ShoulderTapPage";
import { useUser } from '../hooks/useUser';
import { useQueryParams } from '../hooks/useQueryParams';
import { useShoulderTapAnalysis } from '../hooks/useShoulderTapAnalysis';
import { isInIframe, isConnectApp, initializeConnectIfNeeded } from '../utils/iframe';
import { isPreProdEnvironment } from '../utils/environment';
import {
    postToHost,
    subscribeFromHost,
    BridgeEventType,
    CopilotContentPayload,
    ShoulderTapFiredPayload,
    ShoulderTapSequencePayload,
} from '../utils/hostBridge';
import {
    buildShoulderTapFiredContent,
    SHOULDER_TAP_STATIC_CONTENT,
} from '../content/shoulderTapContent';
import {
    serviceHealthMetricForTransition,
    ServiceHealthStatus,
} from '../utils/serviceHealth';
import { api } from '../services/api';
import IframeAuthDialog from './IframeAuthDialog';
import CenteredMessage from './CenteredMessage';
import ErrorBoundary from './ErrorBoundary';
import { useMetrics } from '../hooks/useMetrics';
import { installDiagnostics, breadcrumb } from '../utils/diagnostics';

const mbmOptions = {
    arbManifest,
    defaultLocalizationContext: new LocalizationContextBuilder()
        .withLocale(DEFAULT_LOCALE)
        .withDefaultLocale(DEFAULT_LOCALE)
        .build(),
    resolveBundleUrl: (
        resourcePackPath: string | undefined,
        locale: string,
        bundle: string
    ) => resolveBundleUrlWithFallbacks(resourcePackPath, locale, bundle),
};

const App = () => {
    const { contactId } = useQueryParams();
    const { user, loading, login, logout, error, checkAuthentication } = useUser();
    const { logEvent } = useMetrics();
    const inIframe = isInIframe();
    const inConnect = isConnectApp();
    const [connectInitialized, setConnectInitialized] = useState(false);
    const [authAttempts, setAuthAttempts] = useState(0);
    // Customer state is no longer initialized over HTTP (the /initializeCustomerState
    // endpoint was removed). Session setup now happens via the WebSocket flow, so there
    // is no HTTP init phase here.
    const maxAuthAttempts = 2;

    // Capture uncaught errors and rejections as early as possible. These bypass
    // React error boundaries, and an uncaught one is a leading suspect for the
    // frame going blank and stopping polling. `logEvent` is read via a ref-free
    // closure here because installDiagnostics is idempotent and only ever runs
    // once — a stale WebSocket reference is not a concern since useMetrics
    // resolves the socket at call time.
    const logEventRef = useRef(logEvent);
    logEventRef.current = logEvent;
    useEffect(() => {
        installDiagnostics(() => {
            // Only the event type reaches DataSpring; the error's message and
            // stack are captured in breadcrumbs and the console by
            // installDiagnostics itself.
            logEventRef.current(METRICS_EVENTS.UI_UNCAUGHT_ERROR, { durable: true });
        });
        breadcrumb('app.mounted');
        // If this fires, React tore the tree down. A frame whose renderer died
        // leaves no such record — that asymmetry is the whole point.
        return () => breadcrumb('app.unmounted');
    }, []);

    // Initialize Connect SDK if needed
    useEffect(() => {
        if (inIframe && inConnect && !connectInitialized) {
            initializeConnectIfNeeded()
                .then(() => setConnectInitialized(true))
                .catch(console.error);
        }
    }, [inIframe, inConnect, connectInitialized]);

    // Apply iframe full-width class when in iframe
    useEffect(() => {
        if (inIframe) {
            document.body.classList.add('iframe-full-width');
        } else {
            document.body.classList.remove('iframe-full-width');
        }

        return () => {
            document.body.classList.remove('iframe-full-width');
        };
    }, [inIframe]);
    // Handle authentication based on iframe context
    useEffect(() => {
        if (!loading && !user?.authenticated && !inIframe) {
            console.log('User not authenticated, automatically redirecting to login...');
            setTimeout(() => {
                login();
            }, 500);
        }
    }, [loading, user?.authenticated, login, inIframe]);

    // Publish the static copy bundle so the host can render chrome (the
    // collapsed pill, the offline notice) before any tap has fired. Emitted
    // on mount, and again whenever the host asks — a host that attaches its
    // listener after we load, or remounts its overlay, would otherwise miss
    // the initial emission.
    useEffect(() => {
        const publish = () => postToHost<CopilotContentPayload>(
            BridgeEventType.CopilotContent,
            { content: SHOULDER_TAP_STATIC_CONTENT },
        );
        publish();
        return subscribeFromHost(BridgeEventType.ContentRequest, publish);
    }, []);

    // Poll the backend for shoulder-tap events once we have a contactId
    // and the user is authenticated. The host app is expected to pass
    // contactId as a query param on the iframe URL. When a tap fires,
    // forward it to the host over the postMessage bridge — the host
    // renders the accept/dismiss UI and calls back into us via the
    // same bridge (see subscribe hooks below).
    const shoulderTapContactId = user?.authenticated ? contactId : undefined;
    const {
        tap: shoulderTap,
        taps: shoulderTaps,
        accepted: shoulderTapAccepted,
        isOffline: shoulderTapOffline,
    } = useShoulderTapAnalysis(shoulderTapContactId);

    // The read-to-customer script depends on the whole tap sequence, not the
    // tap that just fired — a later tap can change which script applies. The
    // sequence comes from the service now, so it survives a CCAT/CCHUB swap.
    // A swap loads a new host document and a new iframe, so anything we
    // accumulated in memory here would be gone and we'd resolve the script from
    // a partial sequence without knowing it.
    //
    // These live in a ref so the fire effect below can read them without
    // listing them as deps. Both change on polls where no tap fired, and as
    // deps they'd re-run that effect and send another shoulder-tap-fired — the
    // host would alert again for a tap it already showed. Assigning during
    // render (rather than in an effect) means the fire effect sees the taps from
    // the same response that produced `shoulderTap`, never a stale sequence.
    const shoulderTapSequenceRef = useRef({ taps: shoulderTaps, accepted: shoulderTapAccepted });
    shoulderTapSequenceRef.current = { taps: shoulderTaps, accepted: shoulderTapAccepted };

    useEffect(() => {
        if (!shoulderTap || !shoulderTapContactId) return;
        const { taps, accepted } = shoulderTapSequenceRef.current;
        const content = buildShoulderTapFiredContent(taps);
        console.log('[shoulder-tap] fired', { contactId: shoulderTapContactId, tap: shoulderTap });
        postToHost<ShoulderTapFiredPayload>(BridgeEventType.ShoulderTapFired, {
            contactId: shoulderTapContactId,
            tap: shoulderTap,
            taps,
            accepted,
            content,
        });
    }, [shoulderTap, shoulderTapContactId]);

    // False until the hook has heard from the service at all — which includes a
    // failed config fetch or an ineligible contact, not just a first poll. Keeps
    // us from publishing an empty sequence on mount, before we know whether
    // there are taps. Derived as a boolean rather than depending on
    // `shoulderTapOffline` directly, which would re-emit on every health flip.
    const shoulderTapPolled = shoulderTapOffline !== null;

    // The sequence, published on its own rather than only when a tap fires. The
    // server marks a tap fired on the poll that surfaces it, so an iframe that
    // starts up after that point only ever sees `tap: null` and the fire effect
    // above never runs — without this, a host that swapped mid-contact would
    // show an empty list forever. The hook keeps these deps referentially stable
    // when nothing changed, so this fires on real changes, not once per poll.
    useEffect(() => {
        if (!shoulderTapContactId || !shoulderTapPolled) return;
        postToHost<ShoulderTapSequencePayload>(BridgeEventType.ShoulderTapSequence, {
            contactId: shoulderTapContactId,
            taps: shoulderTaps,
            accepted: shoulderTapAccepted,
            content: buildShoulderTapFiredContent(shoulderTaps),
        });
    }, [shoulderTaps, shoulderTapAccepted, shoulderTapContactId, shoulderTapPolled]);

    // Service health is internal: the host is no longer told about
    // offline/healthy transitions (there is no `service-health-changed` event
    // on the bridge anymore). Each flip is reported as a frontend metric
    // instead, which keeps degradation visible without a host contract.
    //
    // A ref rather than state: nothing renders from this, and a re-render per
    // poll observation would be pure overhead. Reset on contact change so a
    // status carried over from the previous contact can't fabricate a flip.
    const healthStatusRef = useRef<ServiceHealthStatus | null>(null);

    useEffect(() => {
        healthStatusRef.current = null;
    }, [shoulderTapContactId]);

    useEffect(() => {
        // `null` means "not yet determined" — the hook has made no observation,
        // so there is nothing to compare against.
        if (!shoulderTapContactId || shoulderTapOffline === null) return;
        const status: ServiceHealthStatus = shoulderTapOffline ? 'offline' : 'healthy';
        const metric = serviceHealthMetricForTransition(healthStatusRef.current, status);
        healthStatusRef.current = status;
        if (!metric) return;
        console.log('[shoulder-tap] service health', status);
        // The metric carries only its event type, so the status detail lives in
        // a breadcrumb alongside the rest of the poll-loop trail.
        breadcrumb('shoulder-tap.service-health', { status });
        // Durable: an offline flip fires *because* requests are failing, and
        // whatever broke polling may well have taken the WebSocket with it.
        // Dropping the event would lose exactly the case worth measuring.
        logEvent(metric, { durable: true });
    }, [shoulderTapOffline, shoulderTapContactId]);

    // Host-driven accept/dismiss: the host UI owns the buttons; we own
    // the API calls. The effect re-subscribes whenever
    // shoulderTapContactId changes so callbacks always target the
    // current contact.
    useEffect(() => {
        if (!shoulderTapContactId) return;
        const unsubscribeAccept = subscribeFromHost(BridgeEventType.ShoulderTapAccept, () => {
            api.acceptShoulderTap(shoulderTapContactId);
        });
        const unsubscribeDismiss = subscribeFromHost(BridgeEventType.ShoulderTapDismiss, () => {
            // Dismiss ends the sequence: the server returns `taps: []` from
            // here on, so the next tap's script resolves without the
            // dismissed ones. Nothing to clear locally.
            api.dismissShoulderTap(shoulderTapContactId);
        });
        return () => {
            unsubscribeAccept();
            unsubscribeDismiss();
        };
    }, [shoulderTapContactId]);

    const handleIframeAuthComplete = async () => {
        // Re-check authentication after popup closes
        await checkAuthentication();
        setAuthAttempts(prev => prev + 1);
    };

    // TODO: replace with spinners
    if (loading) {
        return <CenteredMessage message="Loading..." />;
    }

    if (!user?.authenticated) {
        if (inIframe && authAttempts < maxAuthAttempts) {
            return <IframeAuthDialog onAuthComplete={handleIframeAuthComplete} authAttempts={authAttempts} />;
        }
        
        if (inIframe && authAttempts >= maxAuthAttempts) {
            return <CenteredMessage message="Quick Network Hiccup! Try this first: Refresh your browser || Still seeing this? Please Share Contact ID with any helpful context [here](https://t.corp.amazon.com/V2004379452/communication)" />;
        }
        
        return <CenteredMessage message="Redirecting to login..." />;
    }

    return (
        <MbmProvider {...mbmOptions}>
            {/*
              The boundary wraps only the visible UI. useShoulderTapAnalysis is
              called above it, so an uncaught render error in the chat or
              shoulder-tap page can no longer unmount the tree and silently kill
              the poll loop. Fallback is deliberately minimal — when running as
              a hidden iframe nobody sees it, and the host keeps receiving taps
              over the bridge regardless.
            */}
            <ErrorBoundary
                boundaryName="page"
                onError={() => logEvent(METRICS_EVENTS.UI_RENDER_ERROR, { durable: true })}
                fallback={
                    <CenteredMessage message="Something went wrong displaying this panel. Refresh your browser. Shoulder-tap alerts are still running in the background." />
                }
            >
                {window.location.pathname === '/shoulder-tap' && isPreProdEnvironment() ? (
                    <ShoulderTapPage user={user} />
                ) : (
                    <ChatPage
                        user={user}
                        contactId={contactId}
                    />
                )}
            </ErrorBoundary>
        </MbmProvider>
    );
};

export default App;
